import React, { useEffect, useMemo, useState } from "react";
import {
  getCompanyBranding,
  updateCompanyBranding,
} from "../companyBrandingApi";

import BillingPanel from "../components/BillingPanel";

const DEFAULT_PRIMARY = "#0B0B0B";
const DEFAULT_SECONDARY = "#D4AF37";

export default function Settings() {
  const [branding, setBranding] = useState({
    name: "",
    logo_url: "",
    primary_color: DEFAULT_PRIMARY,
    secondary_color: DEFAULT_SECONDARY,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [whiteLabelLocked, setWhiteLabelLocked] = useState(false);

  useEffect(() => {
    let active = true;

    async function loadBranding() {
      try {
        setLoading(true);
        setError("");

        const data = await getCompanyBranding();

        if (!active) return;

        setBranding({
          name: data?.name || "",
          logo_url: data?.logo_url || "",
          primary_color:
            data?.primary_color || DEFAULT_PRIMARY,
          secondary_color:
            data?.secondary_color || DEFAULT_SECONDARY,
        });
      } catch (err) {
        if (!active) return;

        setError(
          err?.message ||
            "Unable to load company branding."
        );
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadBranding();

    return () => {
      active = false;
    };
  }, []);

  const previewName = useMemo(() => {
    return branding.name?.trim() || "Your Company";
  }, [branding.name]);

  function handleChange(event) {
    const { name, value } = event.target;

    setBranding((current) => ({
      ...current,
      [name]: value,
    }));

    setError("");
    setSuccess("");
  }

  function handleLogoUpload(event) {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file.");
      return;
    }

    const maxSize = 1.5 * 1024 * 1024;

    if (file.size > maxSize) {
      setError(
        "Logo file is too large. Please use an image under 1.5 MB."
      );
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      setBranding((current) => ({
        ...current,
        logo_url: String(reader.result || ""),
      }));

      setError("");
      setSuccess("");
    };

    reader.onerror = () => {
      setError("Unable to read the selected logo file.");
    };

    reader.readAsDataURL(file);
  }

  function handleRemoveLogo() {
    setBranding((current) => ({
      ...current,
      logo_url: "",
    }));

    setError("");
    setSuccess("");
  }

  async function handleSave(event) {
    event.preventDefault();

    try {
      setSaving(true);
      setError("");
      setSuccess("");
      setWhiteLabelLocked(false);

      const result = await updateCompanyBranding({
        name: branding.name,
        logo_url: branding.logo_url,
        primary_color: branding.primary_color,
        secondary_color: branding.secondary_color,
      });

      const saved = result?.branding || branding;

      setBranding({
        name: saved?.name || "",
        logo_url: saved?.logo_url || "",
        primary_color:
          saved?.primary_color || DEFAULT_PRIMARY,
        secondary_color:
          saved?.secondary_color || DEFAULT_SECONDARY,
      });

      window.dispatchEvent(
        new CustomEvent("company-branding-updated", {
          detail: saved,
        })
      );

      setSuccess(
        "White Label branding saved successfully."
      );
    } catch (err) {
      const message =
        err?.message ||
        "Unable to save company branding.";

      if (
        err?.status === 403 ||
        message.toLowerCase().includes("white label")
      ) {
        setWhiteLabelLocked(true);
      }

      setError(message);
    } finally {
      setSaving(false);
    }
  }

  function ColorControl({
    label,
    name,
    value,
  }) {
    const colorValue =
      value ||
      (name === "primary_color"
        ? DEFAULT_PRIMARY
        : DEFAULT_SECONDARY);

    return (
      <div>
        <label
          style={{
            display: "block",
            fontWeight: "800",
            color: "#334155",
            marginBottom: "8px",
          }}
        >
          {label}
        </label>

        <div
          style={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
          }}
        >
          <div
            style={{
              position: "relative",
              width: "54px",
              height: "48px",
              flex: "0 0 54px",
              borderRadius: "12px",
              border: "2px solid #cbd5e1",
              background: colorValue,
              overflow: "hidden",
              boxShadow: "0 2px 6px rgba(15,23,42,0.08)",
              cursor: "pointer",
            }}
            title={`Choose ${label.toLowerCase()}`}
          >
            <input
              type="color"
              name={name}
              value={colorValue}
              onChange={handleChange}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                opacity: 0,
                cursor: "pointer",
                border: "none",
                padding: 0,
              }}
            />
          </div>

          <input
            name={name}
            value={colorValue}
            onChange={handleChange}
            style={{
              flex: 1,
              minWidth: 0,
              padding: "12px",
              border: "1px solid #cbd5e1",
              borderRadius: "10px",
              fontSize: "14px",
              fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
            }}
          />
        </div>

        <div
          style={{
            marginTop: "7px",
            color: "#64748b",
            fontSize: "12px",
          }}
        >
          Click the color square to open the picker.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: "12px" }}>
        <h1
          style={{
            fontSize: "36px",
            marginBottom: "12px",
          }}
        >
          Settings
        </h1>

        <div style={{ color: "#64748b" }}>
          Loading company branding...
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        maxWidth: "1180px",
        margin: "0 auto",
        paddingBottom: "50px",
      }}
    >
      <BillingPanel />
      <div style={{ marginBottom: "28px" }}>
        <div
          style={{
            color: "#B8860B",
            fontSize: "13px",
            fontWeight: "900",
            letterSpacing: "1.8px",
            textTransform: "uppercase",
            marginBottom: "8px",
          }}
        >
          White Label
        </div>

        <h1
          style={{
            margin: 0,
            fontSize: "40px",
            fontWeight: "900",
            color: "#111827",
          }}
        >
          Company Branding
        </h1>

        <p
          style={{
            marginTop: "10px",
            color: "#64748b",
            fontSize: "17px",
            lineHeight: 1.6,
            maxWidth: "760px",
          }}
        >
          Customize the company name, logo, and brand colors
          shown throughout your Integrity Suite workspace.
        </p>
      </div>

      {error ? (
        <div
          style={{
            background: whiteLabelLocked
              ? "#fff7ed"
              : "#fef2f2",
            border: whiteLabelLocked
              ? "1px solid #fdba74"
              : "1px solid #fecaca",
            color: whiteLabelLocked
              ? "#9a3412"
              : "#991b1b",
            borderRadius: "14px",
            padding: "15px 18px",
            marginBottom: "20px",
            fontWeight: "700",
          }}
        >
          {error}

          {whiteLabelLocked ? (
            <div
              style={{
                marginTop: "6px",
                fontWeight: "500",
              }}
            >
              Company branding editing is available on the
              White Label plan.
            </div>
          ) : null}
        </div>
      ) : null}

      {success ? (
        <div
          style={{
            background: "#ecfdf5",
            border: "1px solid #a7f3d0",
            color: "#065f46",
            borderRadius: "14px",
            padding: "15px 18px",
            marginBottom: "20px",
            fontWeight: "800",
          }}
        >
          {success}
        </div>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "minmax(0, 1.15fr) minmax(320px, 0.85fr)",
          gap: "24px",
          alignItems: "start",
        }}
      >
        <form
          onSubmit={handleSave}
          style={{
            background: "#ffffff",
            border: "1px solid #e5e7eb",
            borderRadius: "22px",
            padding: "26px",
            boxShadow:
              "0 12px 35px rgba(15,23,42,0.06)",
          }}
        >
          <div
            style={{
              fontSize: "22px",
              fontWeight: "900",
              color: "#111827",
              marginBottom: "22px",
            }}
          >
            Branding Controls
          </div>

          <label
            style={{
              display: "block",
              fontWeight: "800",
              color: "#334155",
              marginBottom: "8px",
            }}
          >
            Company Name
          </label>

          <input
            name="name"
            value={branding.name}
            onChange={handleChange}
            placeholder="Your Company Name"
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "14px 16px",
              border: "1px solid #cbd5e1",
              borderRadius: "12px",
              fontSize: "16px",
              marginBottom: "22px",
            }}
          />

          <label
            style={{
              display: "block",
              fontWeight: "800",
              color: "#334155",
              marginBottom: "8px",
            }}
          >
            Company Logo
          </label>

          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            onChange={handleLogoUpload}
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "12px",
              border: "1px solid #cbd5e1",
              borderRadius: "12px",
              background: "#f8fafc",
              marginBottom: "10px",
            }}
          />

          <div
            style={{
              color: "#64748b",
              fontSize: "13px",
              lineHeight: 1.5,
              marginBottom: "18px",
            }}
          >
            PNG, JPG, WEBP, or SVG. Maximum file size:
            1.5 MB.
          </div>

          {branding.logo_url ? (
            <button
              type="button"
              onClick={handleRemoveLogo}
              style={{
                border: "1px solid #fecaca",
                background: "#fff",
                color: "#b91c1c",
                borderRadius: "10px",
                padding: "9px 13px",
                fontWeight: "700",
                cursor: "pointer",
                marginBottom: "22px",
              }}
            >
              Remove Logo
            </button>
          ) : null}

          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(2, minmax(0, 1fr))",
              gap: "18px",
              marginBottom: "26px",
            }}
          >
            <ColorControl
              label="Primary Color"
              name="primary_color"
              value={branding.primary_color}
            />

            <ColorControl
              label="Secondary Color"
              name="secondary_color"
              value={branding.secondary_color}
            />
          </div>

          <button
            type="submit"
            disabled={saving}
            style={{
              width: "100%",
              border: "none",
              borderRadius: "14px",
              padding: "15px 18px",
              background: saving
                ? "#64748b"
                : "#111827",
              color: "#ffffff",
              fontSize: "16px",
              fontWeight: "900",
              cursor: saving
                ? "not-allowed"
                : "pointer",
            }}
          >
            {saving
              ? "Saving Branding..."
              : "Save White Label Branding"}
          </button>
        </form>

        <div
          style={{
            position: "sticky",
            top: "24px",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              fontWeight: "900",
              color: "#64748b",
              letterSpacing: "1px",
              textTransform: "uppercase",
              marginBottom: "10px",
            }}
          >
            Live Preview
          </div>

          <div
            style={{
              background:
                branding.primary_color ||
                DEFAULT_PRIMARY,
              borderRadius: "22px",
              padding: "26px",
              color: "#ffffff",
              minHeight: "390px",
              boxShadow:
                "0 20px 45px rgba(15,23,42,0.18)",
            }}
          >
            {branding.logo_url ? (
              <img
                src={branding.logo_url}
                alt={`${previewName} logo`}
                style={{
                  width: "100%",
                  maxWidth: "220px",
                  maxHeight: "110px",
                  objectFit: "contain",
                  objectPosition: "left center",
                  display: "block",
                  marginBottom: "20px",
                }}
              />
            ) : (
              <div
                style={{
                  width: "74px",
                  height: "74px",
                  borderRadius: "18px",
                  background:
                    branding.secondary_color ||
                    DEFAULT_SECONDARY,
                  color:
                    branding.primary_color ||
                    DEFAULT_PRIMARY,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "900",
                  fontSize: "28px",
                  marginBottom: "20px",
                }}
              >
                {previewName.charAt(0).toUpperCase()}
              </div>
            )}

            <div
              style={{
                fontSize: "25px",
                fontWeight: "900",
                color:
                  branding.secondary_color ||
                  DEFAULT_SECONDARY,
                marginBottom: "6px",
              }}
            >
              {previewName}
            </div>

            <div
              style={{
                color: "#f8fafc",
                opacity: 0.88,
                fontSize: "13px",
                marginBottom: "28px",
              }}
            >
              Credit Repair Management Platform
            </div>

            <div
              style={{
                height: "1px",
                background:
                  "rgba(255,255,255,0.12)",
                marginBottom: "20px",
              }}
            />

            {[
              "Dashboard",
              "Clients",
              "Letters",
              "Disputes",
              "Credit Reports",
              "Reports",
            ].map((item, index) => (
              <div
                key={item}
                style={{
                  padding: "11px 14px",
                  borderRadius: "10px",
                  marginBottom: "7px",
                  background:
                    index === 0
                      ? "rgba(255,255,255,0.08)"
                      : "transparent",
                  color:
                    index === 0
                      ? branding.secondary_color ||
                        DEFAULT_SECONDARY
                      : "#ffffff",
                  fontWeight:
                    index === 0 ? "800" : "600",
                }}
              >
                {item}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
